import { deleteRequest, get, isDev, post, put } from "../../../shared/data";
import { objectToParamString } from "../../../shared/helpers";

export const getCarriers = () => {
    return get("/receiving/carriers").then((data) => {
        return data.map((c) => {
            return { text: c.name, value: c.code };
        });
    });
};

export const getLookups = () => {
    return get("/receiving/lookups").then((response) => {
        return Object.keys(response).reduce((lookups, list) => {
            lookups[list] = response[list].map((c) => {
                return { text: c.text, value: c.code };
            });

            return lookups;
        }, {});
    });
};

export const getClients = (params = {}) => {
    const paramString = objectToParamString(params);

    return get(`/receiving/clients?${paramString}`).then((data) => {
        return data.map((c) => {
            return {
                id: c.id,
                text: c.name,
                value: c.code,
                companyId: c.companyId,
                hasPremiumEditing: c.hasPremiumEditing,
                imagesNeedClientApprovalDefault: c.imagesNeedClientApprovalDefault,
                sendToGdsn: c.sendToGdsn,
                turnAroundDays: c.sla.turnaroundDays,
            };
        });
    });
};

export const getReceivers = (shipmentData) => {
    return get("/receiving/receivers").then((data) => {
        const existingReceiver = shipmentData?.receivedByMatch;
        const isReceiverActive = data.find((c) => c.key === existingReceiver?.key);

        const concatReceivers = !isReceiverActive ? [...data, existingReceiver] : data;

        return concatReceivers.map((c) => {
            return { text: c?.name, value: c?.key, hidden: data.every((items) => items.key !== c?.key) };
        });
    });
};

export const getClientPreferences = (params = {}) => {
    const paramString = objectToParamString(params);

    return get(`/receiving/clients/clientInfo?${paramString}`);
};

export const getSuppliers = (nameStartsWith) => {
    return get(`/receiving/suppliers?nameStartsWith=${nameStartsWith || ""}`).then((data) => {
        return data.map((c) => {
            return { text: c.name, value: c.code };
        });
    });
};

export const getLocations = () => {
    return get("/receiving/locations").then((data) => {
        return data.map((c) => {
            return { text: c.location, value: c.locationValue, companyName: c.companyName };
        });
    });
};

export const getItemMasterShipmentTypes = () => {
    return get("/receiving/legacy-itemmaster-shipment-types").then((data) => {
        return data.map((c) => {
            return { text: c.legacyItemMasterShipmentTypeName, value: c.id };
        });
    });
};

export const getPaymentMethods = () => {
    return get("/receiving/paymentmethods").then((data) => {
        return data.map((c) => {
            return { text: c.name, value: c.code };
        });
    });
};

export const addSupplier = (payload) => {
    return post("/receiving/suppliers", payload);
};

export const updateClient = (payload, settings = {}) => {
    return put("/receiving/clients", payload, settings);
};

function extractObjectValue(obj) {
    if (!obj) {
        return "";
    }

    if (obj.code && obj.code.length > 0) return obj.code;

    if (obj.key && obj.key.length > 0) return obj.key;

    return "";
}
/*
 *** This flattens out the shipment object, if it finds that a value is returned as an object.
 *** I'm looking for properties in this order: Code, Key
 */
export function flattenShipments(shipment) {
    if (!shipment) {
        return null;
    }

    return Object.keys(shipment).reduce((memo, key) => {
        const value = shipment[key];

        if (key === "locationInfo") {
            memo["locationValue"] = value.locationValue;
        } else if (key === "rushDays" || key === "turnAroundDays") {
            memo["turnAroundDays"] = shipment.isRush ? shipment.rushDays : shipment.turnAroundDays;
        } else if (key === "cxHClient") {
            memo["cxHCompany"] = extractObjectValue(value);
            memo["cxHClientId"] = value?.id;
            memo["hasMetricMeasurementsOnly"] = value.hasMetricMeasurementsOnly;
        } else if (key === "carrierTrackingNumbers") {
            memo[key] = value;
        } else if (key === "receivedBy" && value) {
            memo[key] = value.key;
            memo["receivedByMatch"] = { text: value.name, name: value.name, key: value.key, value: value.key };
        } else {
            memo[key] = value !== null && typeof value === "object" ? extractObjectValue(value) : value === undefined ? "" : value;
        }

        return memo;
    }, {});
}

export const getShipment = (shipmentKey) => {
    return get(`/receiving/shipments/${shipmentKey}`).then(flattenShipments);

    /*const id = !shipmentId ? "" : shipmentId;
    if(!id) {
        const payload = {
                            key: id
                        };
        return post('/receiving/shipments/', payload).then(flattenShipments);
    } else {*/
    //}
};

export const saveShipment = (shipment) => {
    if (shipment.isRush) {
        shipment.rushDays = shipment.turnAroundDays;
    }

    if (!shipment.key) {
        return post("/receiving/shipments", shipment, { passthrough: true });
    }

    return put(`/receiving/shipments/${shipment.key}`, shipment, { passthrough: true });
};

export const completeShipment = (shipmentKey, overrideClientProductLimits) => {
    try {
        return post(`/receiving/shipments/completed`, { shipmentKey, overrideClientProductLimits }, { passthrough: true });
    } catch (e) {
        return Promise.reject(e);
    }
};

export const deleteShipment = (shipmentKey) => {
    return deleteRequest(`/receiving/shipments/${shipmentKey}`);
};

export const getDueDate = (date, days) => {
    return get(`/duedate?date=${date}&days=${days}`);
};

export const getSLACounts = (companyId, startDate) => {
    return get(`/sla/clients/${companyId}/product-limits?shipmentStartDate=${startDate}`);
};

export const getProductLimits = (shipmentKey, { date, isForNewUpc } = {}) => {
    const params = {};

    if (date) {
        params.date = date;
    }

    if (isForNewUpc) {
        params.isForNewUpc = true;
    }

    const query = objectToParamString(params);

    return get(`/receiving/shipments/${shipmentKey}/product-limits${query ? `?${query}` : ""}`);
};

// Call when on shipment complete as validation complete
export const getSLADueDate = (shipmentKey) => {
    if (isDev) {
        return Promise.resolve({
            originalDueOn: "",
            calculatedDueOn: "",
            isWithinSla: true,
            hasSla: false,
            businessDaysToDueDate: null,
        });
    }

    return get(`/receiving/shipments/${shipmentKey}/due-date`);
};

export const getCalculatedDueDate = (payload) => {
    return post("/receiving/shipments/due-date-calculation", payload);
};
